import { useState } from "react";
import axios, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const StreamingDebugPanel = ({ user }) => {
  const [videoId, setVideoId] = useState('');
  const [streamKey, setStreamKey] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const testStreaming = async () => {
    if (!videoId || !streamKey) {
      toast.error('Please enter both Video ID and Stream Key');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const response = await axios.post(`${API}/test/stream`, null, {
        params: { video_id: videoId, stream_key: streamKey },
        headers: { Authorization: `Bearer ${user.access_token}` }
      });

      setTestResult(response.data);

      if (response.data.success) {
        toast.success('Streaming test completed successfully!');
      } else {
        toast.error('Streaming test failed. Check debug info below.');
      }
    } catch (error) {
      console.error('Test streaming failed:', error);
      setTestResult({
        success: false,
        error: error.response?.data?.detail || 'Test failed',
        message: 'Request failed'
      });
      toast.error('Test request failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-yellow-800 font-medium">Debug Streaming Pipeline</h4>
        <p className="text-sm text-yellow-700 mt-1">
          Test if video streaming to YouTube Live works correctly
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="video-id">YouTube Video ID</Label>
          <Input
            id="video-id"
            placeholder="Enter YouTube video ID (e.g., dQw4w9WgXcQ)"
            value={videoId}
            onChange={(e) => setVideoId(e.target.value)}
          />
          <p className="text-xs text-gray-600 mt-1">
            Get this from any YouTube URL: youtube.com/watch?v=<strong>VIDEO_ID</strong>
          </p>
          <div className="mt-2 flex flex-wrap gap-1">
            <span className="text-xs text-gray-500">Quick test IDs:</span>
            {['dQw4w9WgXcQ', 'jNQXAC9IVRw', '9bZkp7q19f0'].map(id => (
              <button
                key={id}
                onClick={() => setVideoId(id)}
                className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
              >
                {id}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label htmlFor="stream-key">Stream Key</Label>
          <Input
            id="stream-key"
            type="password"
            placeholder="Enter YouTube Live stream key"
            value={streamKey}
            onChange={(e) => setStreamKey(e.target.value)}
          />
          <p className="text-xs text-gray-600 mt-1">
            Get this from YouTube Studio &rarr; Go Live &rarr; Stream Settings
          </p>
        </div>

        <div className="space-y-2">
          <Button
            onClick={async () => {
              if (!videoId) {
                toast.error('Please enter a Video ID');
                return;
              }

              setTesting(true);
              try {
                const response = await axios.get(`${API}/test/video/${videoId}`, {
                  headers: { Authorization: `Bearer ${user.access_token}` }
                });

                setTestResult(response.data);
                if (response.data.success) {
                  toast.success('Video extraction successful!');
                } else {
                  toast.error('Video extraction failed');
                }
              } catch (error) {
                setTestResult({ success: false, error: 'Request failed', video_id: videoId });
                toast.error('Test request failed');
              } finally {
                setTesting(false);
              }
            }}
            disabled={testing || !videoId}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            {testing ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Testing...
              </div>
            ) : (
              'Test Video Extraction Only'
            )}
          </Button>

          <Button
            onClick={testStreaming}
            disabled={testing || !videoId || !streamKey}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {testing ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Testing Stream...
              </div>
            ) : (
              'Test Full Streaming (30 seconds)'
            )}
          </Button>

          <Button
            onClick={async () => {
              if (!streamKey) {
                toast.error('Please enter a Stream Key');
                return;
              }

              setTesting(true);
              try {
                const response = await axios.post(`${API}/test/simple-stream`, null, {
                  params: { stream_key: streamKey },
                  headers: { Authorization: `Bearer ${user.access_token}` }
                });

                setTestResult(response.data);
                if (response.data.success) {
                  toast.success('Simple test stream started! Check YouTube Studio.');
                } else {
                  toast.error('Simple test stream failed');
                }
              } catch (error) {
                setTestResult({ success: false, error: 'Request failed', stream_key: streamKey });
                toast.error('Test request failed');
              } finally {
                setTesting(false);
              }
            }}
            disabled={testing || !streamKey}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {testing ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Testing...
              </div>
            ) : (
              'Test Simple Pattern Stream (60 seconds)'
            )}
          </Button>

          <Button
            onClick={async () => {
              if (!videoId || !streamKey) {
                toast.error('Please enter both Video ID and Stream Key');
                return;
              }

              setTesting(true);
              try {
                const response = await axios.post(`${API}/test/download-stream`, null, {
                  params: { video_id: videoId, stream_key: streamKey },
                  headers: { Authorization: `Bearer ${user.access_token}` }
                });

                setTestResult(response.data);
                if (response.data.success) {
                  toast.success(`Download+Stream started! File: ${response.data.file_size_mb}MB`);
                } else {
                  toast.error('Download+Stream test failed');
                }
              } catch (error) {
                setTestResult({ success: false, error: 'Request failed' });
                toast.error('Test request failed');
              } finally {
                setTesting(false);
              }
            }}
            disabled={testing || !videoId || !streamKey}
            className="w-full bg-orange-600 hover:bg-orange-700"
          >
            {testing ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Testing...
              </div>
            ) : (
              'Test Download+Stream (60 seconds)'
            )}
          </Button>
        </div>
      </div>

      {testResult && (
        <div className={`border rounded-lg p-4 ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <h4 className={`font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
            Test Result: {testResult.success ? 'Success' : 'Failed'}
          </h4>

          {testResult.message && (
            <p className={`text-sm mt-1 ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
              {testResult.message}
            </p>
          )}

          {testResult.video_url && (
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-700">Video URL:</p>
              <p className="text-xs text-gray-600 break-all">{testResult.video_url}</p>
            </div>
          )}

          {testResult.rtmp_url && (
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-700">RTMP URL:</p>
              <p className="text-xs text-gray-600 break-all">{testResult.rtmp_url}</p>
            </div>
          )}

          {testResult.error && (
            <div className="mt-2">
              <p className="text-xs font-medium text-red-700">Error:</p>
              <p className="text-xs text-red-600">{testResult.error}</p>
            </div>
          )}

          {testResult.extraction_error && (
            <div className="mt-2">
              <p className="text-xs font-medium text-red-700">Extraction Error:</p>
              <p className="text-xs text-red-600">{testResult.extraction_error}</p>
            </div>
          )}

          {testResult.extraction_method && (
            <div className="mt-2">
              <p className="text-xs font-medium text-green-700">Extraction Method:</p>
              <p className="text-xs text-green-600">{testResult.extraction_method}</p>
            </div>
          )}

          {testResult.debug_info && (
            <div className="mt-2">
              <p className="text-xs font-medium text-gray-700">Debug Info:</p>
              <p className="text-xs text-gray-600">{testResult.debug_info}</p>
            </div>
          )}

          {testResult.file_size_mb && (
            <div className="mt-2">
              <p className="text-xs font-medium text-green-700">Downloaded File:</p>
              <p className="text-xs text-green-600">{testResult.file_size_mb}MB - {testResult.temp_file}</p>
            </div>
          )}

          {testResult.process_id && (
            <div className="mt-2">
              <p className="text-xs font-medium text-blue-700">Process Info:</p>
              <p className="text-xs text-blue-600">PID: {testResult.process_id} - Duration: {testResult.stream_duration}</p>
            </div>
          )}

          {testResult.stderr && (
            <div className="mt-2">
              <p className="text-xs font-medium text-red-700">FFmpeg Error Output:</p>
              <pre className="text-xs text-red-600 bg-red-100 p-2 rounded mt-1 overflow-auto">
                {testResult.stderr}
              </pre>
            </div>
          )}
        </div>
      )}

      <div className="bg-gray-50 border rounded-lg p-4">
        <h4 className="font-medium text-gray-800">How to use this:</h4>
        <ol className="text-sm text-gray-600 mt-2 space-y-1 list-decimal list-inside">
          <li><strong>Step 1:</strong> Use one of the quick test video IDs or enter your own <strong>existing/completed</strong> video ID</li>
          <li><strong>Step 2:</strong> Click "Test Video Extraction Only" to verify it works</li>
          <li><strong>Step 3:</strong> Go to YouTube Studio &rarr; Create &rarr; Go Live and copy the Stream Key</li>
          <li><strong>Step 4:</strong> Enter the Stream Key and click "Test Full Streaming"</li>
          <li><strong>Step 5:</strong> Check YouTube Studio to see if the stream appears</li>
        </ol>
        <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-xs text-yellow-800">
            <strong>Important:</strong> Don't use future live events or private videos. Use existing, public videos for testing.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StreamingDebugPanel;
